/**
 * 监听 TCCC 外呼通知栏浮层的出现/消失及位置尺寸变化
 *
 * 背景：TCCC WebSDK 在外呼时会向页面注入一个通知栏浮层，浮层内包含
 * 「呼叫中/通话中」标题与「挂断」按钮（挂断按钮带 part="endCall-button"）。
 *
 * 关键点：该浮层通常渲染在 **Shadow DOM** 内（挂断按钮带 `part` 属性即为 Shadow DOM 特征，
 * 且项目录制回放使用了 shadowDomPlugin），普通 document.querySelector 无法穿透 Shadow DOM。
 * 因此这里通过递归遍历 element + shadowRoot 的方式定位浮层，锚点优先级：
 *   1. `[part="endCall-button"]`（挂断按钮，最稳定）
 *   2. 含「挂断」文案的 `tccc-` 前缀容器（兜底）
 *
 * 返回浮层当前的位置尺寸（相对视口），浮层不存在时返回 null。
 */
import { useEffect, useRef, useState } from 'react'

/** 浮层的位置尺寸（相对视口，等价于 getBoundingClientRect 的子集） */
export interface CallBarRect {
  top: number
  left: number
  width: number
  height: number
}

/** 判断两个 rect 是否发生了有意义的变化（避免亚像素抖动导致频繁 setState） */
const isRectChanged = (a: CallBarRect | null, b: CallBarRect | null): boolean => {
  if (a === b) return false
  if (!a || !b) return true
  return (
    Math.abs(a.top - b.top) > 0.5
    || Math.abs(a.left - b.left) > 0.5
    || Math.abs(a.width - b.width) > 0.5
    || Math.abs(a.height - b.height) > 0.5
  )
}

/** 元素是否含 tccc- 前缀类名 */
const hasTcccClass = (el: Element): boolean => {
  const cls = typeof el.className === 'string' ? el.className : (el.getAttribute?.('class') || '')
  return /(^|\s)tccc-/.test(cls)
}

/**
 * 深度优先遍历（穿透 Shadow DOM），对每个元素执行 visitor。
 * visitor 返回 true 时提前终止并返回该元素。
 */
const deepQuery = (
  root: Document | ShadowRoot | Element,
  visitor: (el: Element) => boolean
): Element | null => {
  const children = (root as Element).children || (root as Document | ShadowRoot).children
  if (!children) return null
  for (let i = 0; i < children.length; i++) {
    const el = children[i]
    if (visitor(el)) return el
    // 穿透 Shadow DOM
    const sr = (el as HTMLElement).shadowRoot
    if (sr) {
      const found = deepQuery(sr, visitor)
      if (found) return found
    }
    // 继续遍历子元素
    const found = deepQuery(el, visitor)
    if (found) return found
  }
  return null
}

/**
 * 从命中的锚点元素向上回溯到浮层最外层容器（同一 shadow root / document 内）。
 * 优先取最外层的 tccc- 容器；若不存在，则退回到 shadow host 或锚点自身。
 */
const resolveCallBarRoot = (anchor: Element): HTMLElement => {
  let node: Element | null = anchor
  let outermostTccc: HTMLElement | null = null
  while (node) {
    if (node instanceof HTMLElement && hasTcccClass(node)) {
      outermostTccc = node
    }
    // 跨越 shadow 边界：ShadowRoot.host 是宿主元素
    const parent: Node | null = node.parentElement
      || (node.parentNode instanceof ShadowRoot ? node.parentNode.host : null)
    node = parent as Element | null
  }
  if (outermostTccc) return outermostTccc
  // 兜底：返回锚点所在的 shadow host（若有），否则返回锚点本身
  const rootNode = anchor.getRootNode()
  if (rootNode instanceof ShadowRoot) return rootNode.host as HTMLElement
  return anchor as HTMLElement
}

/** 查找 TCCC 浮层的根容器（穿透 Shadow DOM） */
const findCallBarRoot = (): HTMLElement | null => {
  // 1) 优先通过挂断按钮锚点 part="endCall-button"
  const endCallBtn = deepQuery(document, (el) => el.getAttribute?.('part') === 'endCall-button')
  if (endCallBtn) {
    return resolveCallBarRoot(endCallBtn)
  }

  // 2) 兜底：含「挂断」文案的 tccc- 容器（取最内层命中的元素再回溯）
  const hangupEl = deepQuery(document, (el) => {
    if (!hasTcccClass(el)) return false
    // 仅匹配直接文本包含「挂断」的按钮/元素，避免匹配到过大的外层
    const text = (el.textContent || '').trim()
    return text.includes('挂断') && text.length < 12
  })
  if (hangupEl) {
    return resolveCallBarRoot(hangupEl)
  }

  return null
}

/** 元素是否可见（在文档中且有尺寸） */
const isVisible = (el: HTMLElement | null): el is HTMLElement => {
  if (!el || !el.isConnected) return false
  const rect = el.getBoundingClientRect()
  return rect.width > 0 && rect.height > 0
}

/** 未找到浮层时的重新查找间隔（ms）。见下方 measure 注释 */
const SEARCH_INTERVAL = 500

/**
 * @param enabled 是否启用监听。无 AI 实时通话辅助权限（tcc.permission.calling.feature-r）
 *   或入口条整体关闭时传 false，此时不做任何查找，并清空已有测量结果。
 */
export function useTcccCallBarRect (enabled = true): CallBarRect | null {
  const [rect, setRect] = useState<CallBarRect | null>(null)
  const rectRef = useRef<CallBarRect | null>(null)
  const rootRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!enabled) {
      rootRef.current = null
      if (rectRef.current !== null) {
        rectRef.current = null
        setRect(null)
      }
      return
    }
    let stopped = false
    let rafId: number | null = null
    let timerId: number | null = null

    const applyRect = (next: CallBarRect | null) => {
      if (isRectChanged(rectRef.current, next)) {
        // 调试：在控制台执行 window.__AI_CALLING_ENTRY_DEBUG__ = true 可观察浮层识别情况
        if ((window as any).__AI_CALLING_ENTRY_DEBUG__) {
          console.log('[AiCallingEntry] callbar rect ->', next, 'root:', rootRef.current)
        }
        rectRef.current = next
        setRect(next)
      }
    }

    /**
     * 测量当前浮层位置，并按「是否已找到浮层」决定下一次调度：
     *   - 已找到：rAF 每帧跟随，保证拖动/滚动/resize 时入口条紧贴浮层；
     *   - 未找到：降频到 SEARCH_INTERVAL 轮询。
     * findCallBarRoot 会深度遍历整个 document 及其所有 shadowRoot，代价较高；
     * 浮层不存在时（坐席大部分时间都不在通话中）每帧全量遍历纯属浪费，故必须降频。
     */
    const measure = () => {
      if (stopped) return

      // 若已有缓存根节点但已失效，则重置
      if (rootRef.current && !isVisible(rootRef.current)) {
        rootRef.current = null
      }
      // 没有缓存根节点则重新查找
      if (!rootRef.current) {
        rootRef.current = findCallBarRoot()
      }

      const root = rootRef.current
      if (isVisible(root)) {
        const r = root.getBoundingClientRect()
        applyRect({ top: r.top, left: r.left, width: r.width, height: r.height })
        rafId = window.requestAnimationFrame(measure)
      } else {
        applyRect(null)
        timerId = window.setTimeout(measure, SEARCH_INTERVAL)
      }
    }

    measure()

    return () => {
      stopped = true
      if (rafId != null) window.cancelAnimationFrame(rafId)
      if (timerId != null) window.clearTimeout(timerId)
    }
  }, [enabled])

  return rect
}
